import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { useCan } from '../auth/useCan'
import { homePathFor } from './paths'
import { areaDoCaminho } from '../areas/areas'
import { areasNavegaveis, primeiraTelaDaArea } from '../areas/acesso'

/** Protege rotas autenticadas. Enquanto carrega, o loader-folha cobre a tela.
 *
 *  Quem entrou com a senha temporária não passa daqui: o backend recusaria cada
 *  chamada com PASSWORD_CHANGE_REQUIRED, e a pessoa veria a aplicação inteira
 *  em erro. O desvio para a criação da senha é o que transforma essa recusa em
 *  um caminho. */
export function RequireAuth() {
  const { status, precisaCriarSenha } = useAuth()
  const location = useLocation()

  if (status === 'loading') return null
  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location }} />
  }
  if (precisaCriarSenha) {
    return <Navigate to="/primeiro-acesso" replace />
  }
  return <Outlet />
}

/** Restringe por lado (staff x cliente); fora do escopo → manda pra home do papel. */
export function RoleGate({ allow, children }: { allow: 'staff' | 'cliente'; children: React.ReactNode }) {
  const { user } = useAuth()
  const isStaff = Boolean(user?.is_staff)
  const allowed = allow === 'staff' ? isStaff : !isStaff
  if (!allowed) return <Navigate to={homePathFor(user)} replace />
  return <>{children}</>
}

/** Layout-guard: só staff passa (usa <Outlet/>). Não-staff volta pra home do papel. */
export function StaffOnly() {
  const { user } = useAuth()
  if (!user?.is_staff) return <Navigate to={homePathFor(user)} replace />
  return <Outlet />
}

/** Layout-guard do portal: só o admin do cliente (customer_admin) gerencia sub-usuários.
 *  O operador do cliente volta pra home do portal. (Segurança real é no backend.) */
export function CustomerAdminOnly() {
  const { user } = useAuth()
  if (user?.role !== 'customer_admin') return <Navigate to="/portal" replace />
  return <Outlet />
}

/** Exige uma permissão para a rota montar. Sem ela, volta pra home do papel.
 *
 *  Faltava por completo: `navConfig` declara a permissão de cada item e
 *  `AppRoutes` consumia só o `path`, jogando `item.permission` no lixo. O menu
 *  escondia a entrada e a URL digitada à mão montava a página inteira, que então
 *  fazia as chamadas e enchia a tela de 403.
 *
 *  Isto é camada de INTERFACE, não de segurança: quem nega de verdade é o
 *  servidor. O que ele não pode fazer é transformar a negativa numa tela útil, e é
 *  disso que este portão cuida. */
export function PermissionGate({ permission, children }: { permission: string; children?: React.ReactNode }) {
  const { user } = useAuth()
  const can = useCan()
  const { pathname } = useLocation()
  if (!can(permission)) return <Navigate to={destinoDaRecusa(user, pathname)} replace />
  return <>{children ?? <Outlet />}</>
}

/** Como o PermissionGate, mas basta UMA das keys: o portão do item de menu que
 *  declara `anyPermission`, como o Início do DP, que mostra o cartão de cada módulo
 *  que a pessoa vê. */
export function AnyPermissionGate({ permissions, children }: { permissions: string[]; children?: React.ReactNode }) {
  const { user } = useAuth()
  const can = useCan()
  const { pathname } = useLocation()
  if (!permissions.some((p) => can(p))) return <Navigate to={destinoDaRecusa(user, pathname)} replace />
  return <>{children ?? <Outlet />}</>
}

/** Para onde vai quem abriu uma tela que não alcança. Dentro de uma área que ele
 *  alcança, fica nela, na primeira tela dela; senão, a home. A primeira tela da
 *  área é sempre uma que ele alcança, então não há laço. */
function destinoDaRecusa(user: Parameters<typeof homePathFor>[0], pathname: string): string {
  const area = areaDoCaminho(pathname)
  if (area && typeof user === 'object' && areasNavegaveis(user).includes(area)) {
    const inicio = primeiraTelaDaArea(area, user)
    if (inicio && inicio !== pathname) return inicio
  }
  return homePathFor(user)
}

/** Redireciona "/" para a home do papel. */
export function HomeRedirect() {
  const { user } = useAuth()
  return <Navigate to={homePathFor(user)} replace />
}
